import { type Merge } from 'type-fest';

type MappedEntries<T extends object> = {
  [K in keyof T]: [K, T[K]];
}[keyof T];

function entries<T extends object>(obj: T): MappedEntries<T>[] {
  return Object.entries(obj) as MappedEntries<T>[];
}

function fromEntries<K extends PropertyKey, V>(
  entries: [K, V][] | (readonly [K, V])[],
): Record<K, V> {
  return Object.fromEntries(entries) as Record<K, V>;
}

function keys<T extends object>(obj: T): (keyof T)[] {
  return Object.keys(obj) as (keyof T)[];
}

function values<T extends object>(obj: T): T[keyof T][] {
  return Object.values(obj) as T[keyof T][];
}

type RecursiveMerge<T extends object[]> = T extends [
  infer First extends object,
  ...infer Rest extends object[],
]
  ? Merge<First, RecursiveMerge<Rest>>
  : unknown;

/**
 * Merges multiple objects into a single object. If the type of each object is statically known,
 * we can merge them recursively to infer the type of the resulting object.
 *
 * Otherwise, this will return `unknown`.
 */
function merge<Objects extends object[]>(...objects: Objects) {
  return objects.reduce((acc, obj) => {
    return { ...acc, ...obj };
  }, {}) as RecursiveMerge<Objects>;
}

/**
 * Utility functions for working with typed objects.
 */
export const typedObject = {
  entries,
  fromEntries,
  keys,
  values,
  merge,
};
